import React, { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { AlertTriangle, Clock, RotateCcw, X } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { payWithCashfree } from "@/lib/cashfree";

/*
 * Shown on whatever page the customer is on after a payment fails or is abandoned.
 *  - retry   : the booking is held for 20 minutes; "Retry payment" reopens Cashfree for the SAME booking.
 *  - expired : the 20 minutes ran out, the booking was cancelled automatically (shown as Failed) and the
 *              customer has to book again.
 * The server is the source of truth (GET /payments/cashfree/retry-window); the countdown is only display.
 */
const DISMISS_KEY = "navi_pay_expired_dismissed";
const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;
const mmss = (s) => `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;

export default function PaymentRetryBar() {
  const { user } = useAuth();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [info, setInfo] = useState(null);
  const [left, setLeft] = useState(0);
  const [busy, setBusy] = useState(false);
  const alive = useRef(true);
  useEffect(() => { alive.current = true; return () => { alive.current = false; }; }, []);

  const hidden = !user || pathname.startsWith("/admin") || pathname.startsWith("/payment/");

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/payments/cashfree/retry-window");
      if (!alive.current) return;
      if (data.state === "expired") {
        let seen = ""; try { seen = localStorage.getItem(DISMISS_KEY) || ""; } catch {}
        setInfo(seen === data.payment_id ? null : data);
      } else if (data.state === "retry") { setInfo(data); setLeft(data.seconds_left || 0); }
      else setInfo(null);
    } catch { /* offline or logged out: show nothing */ }
  }, []);

  useEffect(() => {
    if (hidden) { setInfo(null); return undefined; }
    const t = setTimeout(refresh, 2500);          // after the screen is usable
    return () => clearTimeout(t);
  }, [hidden, pathname, user?.user_id, refresh]);

  // 1 s countdown; when it reaches zero the server cancels the booking and we show the "book again" state.
  useEffect(() => {
    if (info?.state !== "retry") return undefined;
    const t = setInterval(() => setLeft((n) => {
      if (n <= 1) { clearInterval(t); setTimeout(refresh, 1500); return 0; }
      return n - 1;
    }), 1000);
    return () => clearInterval(t);
  }, [info, refresh]);

  if (hidden || !info || info.state === "none") return null;

  const retry = async () => {
    setBusy(true);
    try { await payWithCashfree({ kind: info.kind, id: info.payment_id }); }
    catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "Could not open the payment page");
      refresh();
    } finally { if (alive.current) setBusy(false); }
  };
  const dismiss = () => { try { localStorage.setItem(DISMISS_KEY, info.payment_id); } catch {} setInfo(null); };
  const bookAgain = () => { dismiss(); navigate(info.kind === "addon" ? "/addons" : "/subscription?start=1"); };

  const expired = info.state === "expired" || left <= 0;
  const what = `${info.plan_name ? `${info.plan_name} · ` : ""}${inr(info.amount)}`;
  return (
    <div data-testid={expired ? "payment-expired-bar" : "payment-retry-bar"} role="alert"
      className={`fixed left-1/2 top-3 z-[70] w-[min(94vw,560px)] -translate-x-1/2 rounded-2xl border p-3.5 shadow-xl backdrop-blur ${expired ? "border-red-500/30 bg-red-50/95 dark:bg-red-950/80" : "border-amber-500/40 bg-amber-50/95 dark:bg-amber-950/80"}`}>
      <div className="flex items-start gap-3">
        <span className={`mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-xl ${expired ? "bg-red-500/15 text-red-600" : "bg-amber-500/15 text-amber-600"}`}>
          {expired ? <AlertTriangle className="h-5 w-5" /> : <Clock className="h-5 w-5" />}
        </span>
        <div className="min-w-0 flex-1">
          {expired ? (<>
            <p className="text-sm font-semibold">Payment failed. Booking cancelled.</p>
            <p className="mt-0.5 text-xs text-muted-foreground">{what} was not paid within {info.hold_minutes || 20} minutes, so it was cancelled automatically. Please book again. If your bank shows a debit, raise a ticket from your dashboard.</p>
            <button type="button" onClick={bookAgain} className="mt-2.5 inline-flex min-h-10 items-center gap-1.5 rounded-full bg-primary px-4 text-sm font-semibold text-primary-foreground"><RotateCcw className="h-4 w-4" />Book again</button>
          </>) : (<>
            <p className="text-sm font-semibold">Payment failed. Retry within <span className="tabular-nums" data-testid="payment-retry-timer">{mmss(left)}</span></p>
            <p className="mt-0.5 text-xs text-muted-foreground">{what} is held for you. After that it is cancelled and you will need to book again.</p>
            <button type="button" onClick={retry} disabled={busy} data-testid="payment-retry-btn" className="mt-2.5 inline-flex min-h-10 items-center gap-1.5 rounded-full bg-primary px-4 text-sm font-semibold text-primary-foreground disabled:opacity-60"><RotateCcw className="h-4 w-4" />{busy ? "Opening…" : "Retry payment"}</button>
          </>)}
        </div>
        {expired && <button type="button" onClick={dismiss} aria-label="Dismiss" className="rounded-full p-1 text-muted-foreground hover:bg-black/5"><X className="h-4 w-4" /></button>}
      </div>
    </div>
  );
}
